import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { animated } from "react-spring";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useSelector } from "react-redux";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const PdfViewer = ({ url, openRetagModal, sub_stage, stepSelected }) => {
  const initialScaleLevel = 1;

  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(initialScaleLevel);
  const [rotate, setRotate] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [showRetagButton, setshowRetagButton] = useState(false);

  const user = useSelector((state) => state.user);

  const [isPasswordProtected, setIsPasswordProtected] = useState(false);

  const containerRef = useRef(null);
  const pageRefs = useRef([]);

  useEffect(() => {
    if (
      user.dts_user === "MAKER" &&
      ["1", "2", "3", "4", "12", "14"].includes(sub_stage)
    ) {
      setshowRetagButton(true);
    }
  }, []);

  useEffect(() => reset(), [stepSelected]);

  useEffect(() => {
    const handleScroll = () => {
      if (!pageRefs.current || pageRefs.current.length === 0) return;

      const containerTop = containerRef.current.getBoundingClientRect().top;

      let closestPage = pageNumber;
      let closestOffset = Infinity;

      pageRefs.current.forEach((page, index) => {
        if (page) {
          const pageTop = page.getBoundingClientRect().top - containerTop;
          const offset = Math.abs(pageTop);

          if (offset < closestOffset) {
            closestOffset = offset;
            closestPage = index + 1;
          }
        }
      });

      if (closestPage !== pageNumber) {
        setPageNumber(closestPage);
      }
    };

    const container = containerRef.current;
    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [pageNumber]);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
  }

  const isFirstPage = pageNumber === 1;
  const isLastPage = pageNumber === numPages;

  const previousPage = () => {
    if (!isFirstPage) {
      selectPage(pageNumber - 1);
    }
  };

  const nextPage = () => {
    if (!isLastPage) selectPage(pageNumber + 1);
  };

  const handleDragStart = (e) => {
    setIsDragging(true);

    const angleInRadians = (rotate * Math.PI) / 180;

    const cosAngle = Math.cos(angleInRadians);
    const sinAngle = Math.sin(angleInRadians);

    const adjustedX = position.x * cosAngle - position.y * sinAngle;
    const adjustedY = position.x * sinAngle + position.y * cosAngle;

    setOffset({
      x: e.clientX - adjustedX * scale,
      y: e.clientY - adjustedY * scale,
    });
  };

  const handleDrag = (e) => {
    if (isDragging) {
      const angleInRadians = (rotate * Math.PI) / 180;

      const cosAngle = Math.cos(angleInRadians);
      const sinAngle = Math.sin(angleInRadians);

      const deltaX = (e.clientX - offset.x) / scale;
      const deltaY = (e.clientY - offset.y) / scale;

      const rotatedX = deltaX * cosAngle + deltaY * sinAngle;
      const rotatedY = deltaY * cosAngle - deltaX * sinAngle;

      setPosition({
        x: rotatedX,
        y: rotatedY,
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const reset = () => {
    setScale(initialScaleLevel);
    setRotate(0);
    setPosition({ x: 0, y: 0 });
  };

  useEffect(() => {
    reset();
    setPageNumber(1);
  }, [url]);

  const zoomOut = () => {
    if (scale > 0.25) setScale((prevScale) => Math.max(prevScale - 0.25, 0.25));
  };

  const zoomIn = () => {
    if (scale < 5.0) setScale((prevScale) => Math.min(prevScale + 0.25, 5.0));
  };

  const rotation = () => {
    setRotate((prevRotation) => (prevRotation + 90) % 360);
  };

  const pages = [];

  for (let i = 1; i <= numPages; i++) {
    pages.push(i);
  }

  const selectPage = (pageNum) => {
    setPageNumber(pageNum);

    if (pageRefs.current[pageNum - 1]) {
      const page = pageRefs.current[pageNum - 1];
      const pageTop = page.offsetTop;
      containerRef.current.scrollTo({
        top: pageTop,
        behavior: "smooth",
      });
    }
  };

  const handlePasswordProtection = (cb, reason) => {
    if (reason === 1) {
      setIsPasswordProtected(true);
    } else {
    }
  };

  return (
    <>
      <div
        className="tool-container"
        style={{ position: "sticky", top: 0, zIndex: 800 }}
      >
        <div className="left-controls">
          <button
            className="nav-buttons"
            disabled={pageNumber <= 1}
            onClick={previousPage}
          >
            &lsaquo;
          </button>
          <button
            className="nav-buttons"
            disabled={pageNumber >= numPages}
            onClick={nextPage}
          >
            &rsaquo;
          </button>
          <span>
            <select
              id="toolbar-dropdown"
              onChange={(e) => selectPage(e.target.value)}
              value={pageNumber}
            >
              {pages.map((page, idx) => (
                <option key={idx}>{page}</option>
              ))}
            </select>
            <span className="toolbar-text"> of {numPages}</span>
          </span>
        </div>

        <div className="center-controls">
          <button
            className="nav-buttons"
            onClick={zoomIn}
            disabled={scale >= 5.0}
          >
            +
          </button>
          <span className="zoom-info toolbar-text">
            {(scale * 100).toFixed()}%
          </span>

          <button
            className="nav-buttons"
            onClick={zoomOut}
            disabled={scale <= 0.25}
          >
            -
          </button>
        </div>

        <div className="right-controls">
          <button
            className="nav-buttons"
            onClick={rotation}
            title="Rotate"
            disabled={isPasswordProtected}
          >
            &#8635;
          </button>
          <button className="nav-buttons" onClick={reset}>
            Reset
          </button>

          {showRetagButton && (
            <button className="nav-buttons" onClick={openRetagModal}>
              retag
            </button>
          )}
        </div>
      </div>

      <div
        onMouseDown={handleDragStart}
        onMouseMove={handleDrag}
        onMouseUp={handleDragEnd}
        ref={containerRef}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          width: "100%",
          height: "100%",
          overflow: "auto",
        }}
      >
        <animated.div
          className="img"
          style={{
            transform: `scale(${scale}) rotate(${rotate}deg) translate(${position.x}px, ${position.y}px)`,
            transition: isDragging ? "none" : "transform 0.3s ease",
            width: "100%",
            height: "100%",
            ...(isPasswordProtected && {
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }),
            fontWeight: 800,
            fontSize: "15px",
          }}
        >
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              isPasswordProtected
                ? "PDF is password protected"
                : "Loading PDF..."
            }
            error="Error loading PDF."
            onPassword={handlePasswordProtection}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <div
                key={`page_${index + 1}`}
                ref={(el) => (pageRefs.current[index] = el)}
              >
                <Page
                  pageNumber={index + 1}
                  scale={scale}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                />
              </div>
            ))}
          </Document>
        </animated.div>
      </div>
    </>
  );
};

export default PdfViewer;
